// views/notice/NoticeEditView.tsx
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { getNoticeApi, updateNoticeApi, deleteNoticeApi, deleteAttachmentApi, uploadImageApi, NoticeUpdateRequest, Notice, NoticeAttachment } from '../../lib/api/notice';
import './NoticeForm.css';

// 서버 업로드 어댑터 클래스
class ServerUploadAdapter {
    private loader: any;

    constructor(loader: any) {
        this.loader = loader;
    }

    upload() {
        return this.loader.file.then((file: File) => new Promise<{default: string}>((resolve, reject) => {
            const formData = new FormData();
            formData.append('upload', file);

            console.log('CKEditor image upload 시작:', file.name, file.size, file.type);

            uploadImageApi(file)
            .then(data => {
                console.log('업로드 응답 데이터:', data);
                if (data.url) {
                    const imageUrl = `${import.meta.env.VITE_APP_API_URL}${data.url}`;
                    console.log('최종 이미지 URL:', imageUrl);
                    resolve({
                        default: imageUrl
                    });
                } else {
                    console.error('응답 데이터에 URL이 없습니다:', data);
                    reject('이미지 URL을 받을 수 없습니다.');
                }
            })
            .catch(error => {
                console.error('Image upload failed:', error);
                reject(`이미지 업로드에 실패했습니다: ${error.message || error}`);
            });
        }));
    }

    abort() {
        console.log('이미지 업로드 중단');
    }
}

interface NoticeData {
  id: number;
  title: string;
  content: string;
  isFixed: boolean;
  existingAttachments: NoticeAttachment[];
  newAttachments: File[];
  author: string;
  createdAt: string;
}

const NoticeEditView = () => {
    console.log('NoticeEditView 렌더링 시작');
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    console.log('URL에서 추출한 ID:', id);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [activeTab, setActiveTab] = useState('공지사항');
    const [loading, setLoading] = useState(true);
    
    // 폼 데이터
    const [formData, setFormData] = useState({
        title: '',
        content: '',
        author: '',
        isFixed: false,
        existingAttachments: [] as NoticeAttachment[],
        newAttachments: [] as File[]
    });

    // 에러 상태
    const [errors, setErrors] = useState<Record<string, string>>({});

    // 데이터 로드
    useEffect(() => {
        loadNoticeData();
    }, [id]);

    const loadNoticeData = async () => {
        try {
            console.log('공지사항 로딩 시작, ID:', id);
            setLoading(true);
            if (!id) {
                console.error('ID가 없습니다');
                alert('올바르지 않은 공지사항입니다.');
                navigate('/notice');
                return;
            }

            console.log('API 호출 시작');
            // 실제 API 호출
            const notice: Notice = await getNoticeApi(parseInt(id));
            console.log('API 응답:', notice);

            setFormData({
                title: notice.title,
                content: notice.content,
                isFixed: notice.isFixed,
                existingAttachments: notice.attachments || [],
                newAttachments: [], // 새로 업로드할 파일들
                author: notice.author
            });

            console.log('폼 데이터 설정 완료');

        } catch (error) {
            console.error('공지사항 로드 실패:', error);
            // 에러 상세 정보 표시
            if (error instanceof Error) {
                console.error('Error message:', error.message);
                console.error('Error stack:', error.stack);
            }
            alert('공지사항을 불러오는데 실패했습니다.');
            navigate('/notice');
        } finally {
            console.log('로딩 상태 해제');
            setLoading(false);
        }
    };

    const handleTabChange = (tab: string) => {
        if (tab === "관리자정보 등록") {
            navigate("/admin");
            return;
        }
        setActiveTab(tab);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => {
            return {
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            };
        });

        // 에러 메시지 제거
        if (errors[name]) {
            setErrors(prev => {
                return {
                    ...prev,
                    [name]: ''
                };
            });
        }
    };

    const handleEditorChange = (event: any, editor: any) => {
        const data = editor.getData();
        setFormData(prev => {
            return {
                ...prev,
                content: data
            };
        });

        // 에러 메시지 제거
        if (errors.content) {
            setErrors(prev => {
                return {
                    ...prev,
                    content: ''
                };
            });
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        setFormData(prev => {
            return {
                ...prev,
                newAttachments: [...prev.newAttachments, ...files]
            };
        });
    };

    const removeNewFile = (index: number) => {
        setFormData(prev => {
            return {
                ...prev,
                newAttachments: prev.newAttachments.filter((_, i) => i !== index)
            };
        });
    };

    const removeExistingFile = async (attachmentId: number) => {
        if (window.confirm('첨부파일을 삭제하시겠습니까?')) {
            try {
                await deleteAttachmentApi(attachmentId);
                setFormData(prev => ({
                    ...prev,
                    existingAttachments: prev.existingAttachments.filter(att => att.id !== attachmentId)
                }));
                alert('첨부파일이 삭제되었습니다.');
            } catch (error) {
                console.error('첨부파일 삭제 실패:', error);
                alert('첨부파일 삭제에 실패했습니다.');
            }
        }
    };

    const validate = () => {
        const newErrors: Record<string, string> = {};

        if (!formData.title.trim()) {
            newErrors.title = '제목을 입력해주세요.';
        }

        if (!formData.content.trim()) {
            newErrors.content = '내용을 입력해주세요.';
        }

        return newErrors;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const newErrors = validate();
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        try {
            if (!id) return;

            // API 요청 데이터 준비
            const noticeData: NoticeUpdateRequest = {
                title: formData.title,
                content: formData.content,
                author: formData.author,
                isFixed: formData.isFixed
            };

            // 공지사항 수정 API 호출
            await updateNoticeApi(parseInt(id), noticeData, formData.newAttachments);
            
            alert('공지사항이 성공적으로 수정되었습니다.');
            navigate('/notice');
        } catch (error: any) {
            console.error('수정 실패:', error);
            alert('공지사항 수정에 실패했습니다. 다시 시도해주세요.');
        }
    };

    const handleCancel = () => {
        if (window.confirm('수정중인 내용이 모두 취소됩니다. 정말 취소하시겠습니까?')) {
            navigate('/notice');
        }
    };

    const handleDelete = async () => {
        if (window.confirm('이 공지사항을 정말 삭제하시겠습니까?\n삭제된 내용은 복구할 수 없습니다.')) {
            try {
                if (!id) return;
                
                // 삭제 API 호출
                await deleteNoticeApi(parseInt(id));
                alert('공지사항이 삭제되었습니다.');
                navigate('/notice');
            } catch (error) {
                console.error('삭제 실패:', error);
                alert('공지사항 삭제에 실패했습니다.');
            }
        }
    };

    // CKEditor 설정 - 이미지 업로드 포함
    const editorConfig = {
        toolbar: [
            'heading',
            '|',
            'bold',
            'italic',
            '|',
            'bulletedList',
            'numberedList',
            '|',
            'blockQuote',
            'link',
            'insertTable',
            '|',
            'imageUpload',
            '|',
            'undo',
            'redo'
        ],
        licenseKey: 'GPL',
        image: {
            toolbar: [
                'imageTextAlternative',
                '|',
                'imageStyle:inline',
                'imageStyle:wrapText',
                'imageStyle:breakText'
            ]
        }
    };

    // 업로드 어댑터를 설정하는 플러그인 함수
    function CustomUploadAdapterPlugin(editor: any) {
        editor.plugins.get('FileRepository').createUploadAdapter = (loader: any) => {
            return new ServerUploadAdapter(loader);
        };
    }

    if (loading) {
        return (
            <div className="notice-form-container">
                <div className="notice-content-box">
                    <div className="notice-content">
                        <div className="loading-message">공지사항 정보를 불러오는 중...</div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="notice-form-container">
            {/* 탭 버튼 */}
            <div className="tab-buttons">
                <div className="" style={{ float: "left" }}>
                    <button
                        className={`tab-button ${activeTab === '공지사항' ? 'active' : ''}`}
                        onClick={() => handleTabChange('공지사항')}
                    >
                        공지사항
                    </button>
                    <button
                        className={`tab-button ${activeTab === '관리자정보 등록' ? 'active' : ''}`}
                        onClick={() => handleTabChange('관리자정보 등록')}
                    >
                        관리자정보 등록
                    </button>
                    <button
                        className={`tab-button ${activeTab === '로고 등록' ? 'active' : ''}`}
                        onClick={() => handleTabChange('로고 등록')}
                    >
                        로고 등록
                    </button>
                </div>
            </div>

            {/* 컨텐츠 박스 */}
            <div className="notice-content-box">
                <div className="notice-content">
                    {/* 헤더 */}
                    <div className="content-header">
                        <h1 className="page-title">공지사항 수정</h1>
                    </div>

                    {/* 폼 영역 */}
                    <form onSubmit={handleSubmit}>
                        <table className="form-table">
                            <tbody>
                            {/* 제목 */}
                            <tr>
                                <td className="label-cell">제목 <span className="required">*</span></td>
                                <td className="input-cell">
                                    <input
                                        type="text"
                                        name="title"
                                        value={formData.title}
                                        onChange={handleInputChange}
                                        className="form-input"
                                        placeholder="제목을 입력하세요"
                                    />
                                    {errors.title && <span className="error-message">{errors.title}</span>}
                                </td>
                            </tr>

                            {/* 상단고정 */}
                            <tr>
                                <td className="label-cell">상단고정</td>
                                <td className="input-cell">
                                    <label className="checkbox-wrapper">
                                        <input
                                            type="checkbox"
                                            name="isFixed"
                                            checked={formData.isFixed}
                                            onChange={handleInputChange}
                                            className="form-checkbox"
                                        />
                                        <span className="checkbox-text">상단에 고정하여 표시</span>
                                    </label>
                                </td>
                            </tr>

                            {/* 첨부파일 */}
                            <tr>
                                <td className="label-cell">첨부파일</td>
                                <td className="input-cell">
                                    <div className="file-upload-section">
                                        <button
                                            type="button"
                                            onClick={() => fileInputRef.current?.click()}
                                            className="file-upload-btn"
                                        >
                                            파일 선택
                                        </button>
                                        <input
                                            type="file"
                                            ref={fileInputRef}
                                            onChange={handleFileChange}
                                            multiple
                                            style={{ display: 'none' }}
                                            accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx,.hwp,.txt"
                                        />
                                        <span className="file-help-text">
                                            (이미지, PDF, 문서파일 등 업로드 가능)
                                        </span>
                                    </div>
                                    
                                    {/* 첨부파일 목록 */}
                                    {/* 기존 첨부파일 */}
                                    {formData.existingAttachments.length > 0 && (
                                        <div className="attached-files">
                                            <div className="file-section-title">기존 첨부파일</div>
                                            {formData.existingAttachments.map((attachment) => (
                                                <div key={`existing-${attachment.id}`} className="file-item">
                                                    <span className="file-name">{attachment.fileName}</span>
                                                    <span className="file-size">({Math.round(attachment.fileSize / 1024)}KB)</span>
                                                    <button
                                                        type="button"
                                                        onClick={() => removeExistingFile(attachment.id)}
                                                        className="file-remove-btn"
                                                    >
                                                        삭제
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    
                                    {/* 새로 추가한 첨부파일 */}
                                    {formData.newAttachments.length > 0 && (
                                        <div className="attached-files">
                                            <div className="file-section-title">새로 추가한 첨부파일</div>
                                            {formData.newAttachments.map((file, index) => (
                                                <div key={`new-${index}`} className="file-item">
                                                    <span className="file-name">{file.name}</span>
                                                    <span className="file-size">({Math.round(file.size / 1024)}KB)</span>
                                                    <button
                                                        type="button"
                                                        onClick={() => removeNewFile(index)}
                                                        className="file-remove-btn"
                                                    >
                                                        삭제
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </td>
                            </tr>

                            {/* 내용 */}
                            <tr>
                                <td className="label-cell content-label">내용 <span className="required">*</span></td>
                                <td className="input-cell">
                                    <div className="editor-wrapper">
                                        <CKEditor
                                            editor={ClassicEditor as any}
                                            config={{
                                                ...editorConfig,
                                                extraPlugins: [CustomUploadAdapterPlugin]
                                            }}
                                            data={formData.content}
                                            onChange={handleEditorChange}
                                            onReady={(editor: any) => {
                                                console.log('CKEditor 로딩 완료', editor);
                                            }}
                                            onError={(error: any, { willEditorRestart }: any) => {
                                                console.error('CKEditor 에러:', error);
                                                if (willEditorRestart) {
                                                    console.log('CKEditor 재시작 중...');
                                                }
                                            }}
                                        />
                                    </div>
                                    {errors.content && <span className="error-message">{errors.content}</span>}
                                </td>
                            </tr>
                            </tbody>
                        </table>

                        {/* 버튼 영역 */}
                        <div className="form-actions">
                            <button type="button" onClick={handleDelete} className="btn-delete">
                                삭제
                            </button>
                            <button type="button" onClick={handleCancel} className="btn-cancel">
                                취소
                            </button>
                            <button type="submit" className="btn-submit">
                                수정완료
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default NoticeEditView;